/**
 * Cache Recovery Service
 * 
 * Provides error recovery, fallback mechanisms, and self-healing capabilities
 * for the Phase 4 cache invalidation system. Ensures system resilience in production.
 * 
 * Phase 5 implementation - production error recovery and fallback mechanisms
 */

import { QueryClient } from '@tanstack/react-query';
import { 
  performSelectiveInvalidation, 
  invalidateWithPreset, 
  INVALIDATION_PRESETS,
  type SelectiveInvalidationResult 
} from './selectiveInvalidationService';
import { 
  migrateLegacyKeys, 
  clearNamespaceStorage, 
  needsMigration,
  type StorageResult 
} from './namespacedStorageService';
import { trackCacheOperation } from './cacheMonitoringService';
import { detectCommunityContext } from './communityAwareCacheInvalidation';

// Environment detection
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Recovery strategy types
 */
export type RecoveryStrategy = 
  | 'retry'           // Retry the same operation
  | 'fallback'        // Use alternative approach
  | 'degraded'        // Continue with reduced functionality
  | 'reset'           // Nuclear reset as last resort
  | 'skip';           // Skip the operation entirely

/**
 * Recovery operation context
 */
export interface RecoveryContext {
  operation: string;
  attempt: number;
  maxAttempts: number;
  error: Error;
  metadata?: Record<string, any>;
  fallbacksAvailable: string[];
}

/**
 * Recovery result
 */
export interface RecoveryResult<T = any> {
  success: boolean;
  result?: T;
  strategy: RecoveryStrategy;
  attemptsUsed: number;
  fallbacksUsed: string[];
  errors: string[];
  degraded: boolean;
}

/**
 * Fallback configuration
 */
interface FallbackConfig {
  maxRetries: number;
  retryDelays: number[]; // Delays in ms for each retry
  fallbackStrategies: RecoveryStrategy[];
  skipOnErrors: string[]; // Error messages that should skip recovery
}

/**
 * Default fallback configurations
 */
const DEFAULT_FALLBACK_CONFIGS: Record<string, FallbackConfig> = {
  storage_operation: {
    maxRetries: 3,
    retryDelays: [100, 500, 1000],
    fallbackStrategies: ['retry', 'fallback', 'skip'],
    skipOnErrors: ['QuotaExceededError', 'SecurityError']
  },
  
  selective_invalidation: {
    maxRetries: 2,
    retryDelays: [200, 1000],
    fallbackStrategies: ['retry', 'fallback', 'degraded'],
    skipOnErrors: ['Permission denied']
  },
  
  migration: {
    maxRetries: 2,
    retryDelays: [500, 2000],
    fallbackStrategies: ['retry', 'fallback', 'skip'],
    skipOnErrors: ['Storage corruption']
  },
  
  atomic_reset: {
    maxRetries: 1,
    retryDelays: [1000],
    fallbackStrategies: ['retry', 'reset'],
    skipOnErrors: []
  }
};

/**
 * Recovery service class
 */
class CacheRecoveryService {
  private recoveryAttempts = new Map<string, number>();
  private lastRecoveryTime = new Map<string, number>();
  private readonly recoveryWindow = 60000; // 1 minute window for recovery attempts

  /**
   * Execute operation with automatic recovery
   */
  async executeWithRecovery<T>(
    operationName: string,
    operation: () => Promise<T>,
    config?: Partial<FallbackConfig>
  ): Promise<RecoveryResult<T>> {
    const fullConfig = {
      ...DEFAULT_FALLBACK_CONFIGS[operationName] || DEFAULT_FALLBACK_CONFIGS.storage_operation,
      ...config
    };

    const result: RecoveryResult<T> = {
      success: false,
      strategy: 'retry',
      attemptsUsed: 0,
      fallbacksUsed: [],
      errors: [],
      degraded: false
    };

    // Check if we're in a recovery window
    const operationKey = `${operationName}_${Date.now()}`;
    const currentTime = Date.now();
    const lastRecovery = this.lastRecoveryTime.get(operationName) || 0;
    
    if (currentTime - lastRecovery < this.recoveryWindow) {
      const attempts = this.recoveryAttempts.get(operationName) || 0;
      if (attempts >= fullConfig.maxRetries) {
        result.errors.push('Too many recent recovery attempts');
        return result;
      }
    }

    for (let attempt = 1; attempt <= fullConfig.maxRetries + 1; attempt++) {
      result.attemptsUsed = attempt;
      
      try {
        // Add delay for retry attempts
        if (attempt > 1) {
          const delay = fullConfig.retryDelays[attempt - 2] || 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          
          if (isDevelopment) {
            console.log(`CacheRecovery: Retry attempt ${attempt} for ${operationName} after ${delay}ms`);
          }
        }

        const operationResult = await trackCacheOperation(
          operationName as any,
          operation,
          { recovery: true, attempt }
        );
        
        result.success = true;
        result.result = operationResult;
        
        // Clear recovery tracking on success
        this.recoveryAttempts.delete(operationName);
        this.lastRecoveryTime.delete(operationName);
        
        return result;
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        result.errors.push(`Attempt ${attempt}: ${errorMessage}`);
        
        // Check if this error should skip recovery
        if (fullConfig.skipOnErrors.some(skipError => errorMessage.includes(skipError))) {
          result.errors.push(`Skipping recovery due to: ${errorMessage}`);
          break;
        }

        // Track recovery attempts
        this.recoveryAttempts.set(operationName, attempt);
        this.lastRecoveryTime.set(operationName, currentTime);

        // On final attempt, try fallback strategies
        if (attempt === fullConfig.maxRetries + 1) {
          const recoveryContext: RecoveryContext = {
            operation: operationName,
            attempt,
            maxAttempts: fullConfig.maxRetries + 1,
            error: error instanceof Error ? error : new Error(String(error)),
            fallbacksAvailable: fullConfig.fallbackStrategies
          };

          const fallbackResult = await this.executeFallbackStrategy(recoveryContext);
          if (fallbackResult.success) {
            result.success = true;
            result.result = fallbackResult.result;
            result.strategy = fallbackResult.strategy;
            result.fallbacksUsed = fallbackResult.fallbacksUsed;
            result.degraded = fallbackResult.degraded;
          }
        }
      }
    }

    return result;
  }

  /**
   * Execute fallback strategy
   */
  private async executeFallbackStrategy<T>(context: RecoveryContext): Promise<RecoveryResult<T>> {
    const result: RecoveryResult<T> = {
      success: false,
      strategy: 'fallback',
      attemptsUsed: context.attempt,
      fallbacksUsed: [],
      errors: [...context.error ? [context.error.message] : []],
      degraded: false
    };

    for (const strategy of context.fallbacksAvailable) {
      try {
        result.fallbacksUsed.push(strategy);
        
        switch (strategy) {
          case 'fallback':
            const fallbackResult = await this.executeFallbackOperation(context.operation);
            if (fallbackResult) {
              result.success = true;
              result.result = fallbackResult;
              result.strategy = 'fallback';
              return result;
            }
            break;
            
          case 'degraded':
            const degradedResult = await this.executeDegradedOperation(context.operation);
            if (degradedResult) {
              result.success = true;
              result.result = degradedResult;
              result.strategy = 'degraded';
              result.degraded = true;
              return result;
            }
            break;
            
          case 'reset':
            const resetResult = await this.executeResetFallback();
            if (resetResult) {
              result.success = true;
              result.result = resetResult;
              result.strategy = 'reset';
              result.degraded = true;
              return result;
            }
            break;
            
          case 'skip':
            result.success = true;
            result.strategy = 'skip';
            return result;
        }
        
      } catch (error) {
        result.errors.push(`Fallback ${strategy}: ${error instanceof Error ? error.message : error}`);
      }
    }

    return result;
  }

  /**
   * Execute fallback operation based on operation type
   */
  private async executeFallbackOperation(operationName: string): Promise<any> {
    switch (operationName) {
      case 'selective_invalidation':
        // Fallback to basic localStorage clearing
        return this.fallbackToBasicClearing();
        
      case 'migration':
        // Fallback to marking migration as completed without full migration
        return this.fallbackMigrationMarker();
        
      case 'storage_operation':
        // Fallback to sessionStorage or memory storage
        return this.fallbackToSessionStorage();
        
      default:
        return null;
    }
  }

  /**
   * Execute degraded operation - limited functionality but functional
   */
  private async executeDegradedOperation(operationName: string): Promise<any> {
    if (isDevelopment) {
      console.warn(`CacheRecovery: Entering degraded mode for ${operationName}`);
    }

    switch (operationName) {
      case 'selective_invalidation':
        // Only clear critical keys, preserve everything else
        return this.degradedInvalidation();
        
      case 'migration':
        // Skip migration, use legacy keys as-is
        return { success: true, data: { migrated: [], errors: ['Degraded: Migration skipped'] }};
        
      default:
        return { success: true, degraded: true };
    }
  }

  /**
   * Execute nuclear reset as last resort
   */
  private async executeResetFallback(): Promise<any> {
    console.warn('CacheRecovery: Executing nuclear reset fallback');
    
    try {
      // Clear all terrarium keys
      const allKeys = Object.keys(localStorage);
      const terrariumKeys = allKeys.filter(key => key.startsWith('terrarium'));
      
      terrariumKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          // Continue even if individual key removal fails
        }
      });

      return {
        success: true,
        keysCleared: terrariumKeys.length,
        nuclear: true
      };
    } catch (error) {
      throw new Error(`Nuclear reset failed: ${error}`);
    }
  }

  /**
   * Fallback to basic localStorage clearing
   */
  private async fallbackToBasicClearing(): Promise<SelectiveInvalidationResult> {
    const startTime = performance.now();
    const communityContext = detectCommunityContext();
    
    const basicKeys = [
      'terrarium_community_branding_v4',
      'terrarium_critical_branding_v4',
      'terrarium_meta_version',
      'terrarium_db_boot_time'
    ];

    const cleared = [];
    const errors = [];

    for (const key of basicKeys) {
      try {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          cleared.push(key);
        }
      } catch (error) {
        errors.push(`Failed to clear ${key}: ${error}`);
      }
    }

    return {
      success: errors.length === 0,
      scope: 'global',
      strategy: 'aggressive',
      storageKeysCleared: cleared,
      queryKeysCleared: [],
      brandingInvalidated: cleared.some(key => key.includes('branding')),
      communityContext,
      preservedKeys: [],
      errors,
      durationMs: performance.now() - startTime,
      dryRun: false
    };
  }

  /**
   * Fallback migration marker
   */
  private async fallbackMigrationMarker(): Promise<StorageResult<{ migrated: string[], errors: string[] }>> {
    try {
      localStorage.setItem('terrarium:migration_completed', JSON.stringify({
        completed: true,
        timestamp: Date.now(),
        version: 1,
        fallback: true
      }));

      return {
        success: true,
        data: {
          migrated: [],
          errors: ['Migration completed via fallback marker']
        }
      };
    } catch (error) {
      return {
        success: false,
        error: `Fallback migration marker failed: ${error}`,
        data: { migrated: [], errors: [String(error)] }
      };
    }
  }

  /**
   * Fallback to sessionStorage
   */
  private async fallbackToSessionStorage(): Promise<any> {
    // For future implementation - use sessionStorage when localStorage fails
    return { success: true, fallback: 'sessionStorage' };
  }

  /**
   * Degraded invalidation - only clear critical keys
   */
  private async degradedInvalidation(): Promise<SelectiveInvalidationResult> {
    const startTime = performance.now();
    const communityContext = detectCommunityContext();
    
    // Only clear absolutely necessary keys
    const criticalKeys = ['terrarium_meta_version'];
    const cleared = [];

    for (const key of criticalKeys) {
      try {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          cleared.push(key);
        }
      } catch (error) {
        // Continue with degraded functionality
      }
    }

    return {
      success: true,
      scope: 'custom',
      strategy: 'conservative',
      storageKeysCleared: cleared,
      queryKeysCleared: [],
      brandingInvalidated: false,
      communityContext,
      preservedKeys: [],
      errors: ['Degraded mode: Only critical keys cleared'],
      durationMs: performance.now() - startTime,
      dryRun: false
    };
  }

  /**
   * Get recovery statistics
   */
  getRecoveryStats(): {
    activeRecoveries: number;
    recentFailures: string[];
    recoveryWindowActive: boolean;
  } {
    const currentTime = Date.now();
    const activeRecoveries = Array.from(this.lastRecoveryTime.entries())
      .filter(([_, time]) => currentTime - time < this.recoveryWindow)
      .length;

    const recentFailures = Array.from(this.recoveryAttempts.keys());

    return {
      activeRecoveries,
      recentFailures,
      recoveryWindowActive: activeRecoveries > 0
    };
  }
}

// Global recovery service instance
const recoveryService = new CacheRecoveryService();

/**
 * Execute operation with automatic recovery
 */
export async function executeWithRecovery<T>(
  operationName: string,
  operation: () => Promise<T>,
  config?: Partial<FallbackConfig>
): Promise<RecoveryResult<T>> {
  return recoveryService.executeWithRecovery(operationName, operation, config);
}

/**
 * Enhanced selective invalidation with recovery
 */
export async function selectiveInvalidationWithRecovery(
  queryClient: QueryClient | null,
  presetName: keyof typeof INVALIDATION_PRESETS,
  overrides?: any
): Promise<RecoveryResult<SelectiveInvalidationResult>> {
  return executeWithRecovery(
    'selective_invalidation',
    () => invalidateWithPreset(queryClient, presetName, overrides),
    {
      maxRetries: 2,
      retryDelays: [200, 1000],
      fallbackStrategies: ['retry', 'fallback', 'degraded']
    }
  );
}

/**
 * Enhanced migration with recovery
 */
export async function migrationWithRecovery(): Promise<RecoveryResult<StorageResult<{ migrated: string[], errors: string[] }>>> {
  return executeWithRecovery(
    'migration',
    migrateLegacyKeys,
    {
      maxRetries: 2,
      retryDelays: [500, 2000],
      fallbackStrategies: ['retry', 'fallback', 'skip']
    }
  );
}

/**
 * Get recovery service status
 */
export function getRecoveryStatus() {
  return recoveryService.getRecoveryStats();
}

/**
 * Development utilities
 */
export const devRecoveryUtils = isDevelopment ? {
  /**
   * Test recovery mechanisms
   */
  async testRecovery(): Promise<void> {
    console.log('Testing recovery mechanisms...');
    
    // Test retry recovery
    const retryResult = await executeWithRecovery('storage_operation', async () => {
      if (Math.random() < 0.7) {
        throw new Error('Simulated failure');
      }
      return { test: 'success' };
    });
    
    console.log('Retry recovery test:', retryResult);

    // Test fallback recovery
    const fallbackResult = await executeWithRecovery('selective_invalidation', async () => {
      throw new Error('Force fallback test');
    });
    
    console.log('Fallback recovery test:', fallbackResult);
  },

  /**
   * Force recovery mode for testing
   */
  forceRecoveryMode(): void {
    console.log('Forcing recovery mode for testing...');
    // Simulate multiple failures to trigger recovery
    for (let i = 0; i < 5; i++) {
      executeWithRecovery('storage_operation', () => {
        throw new Error(`Test failure ${i + 1}`);
      }).catch(() => {});
    }
  }
} : {};